import type { StartVisitCounselDepartureAddress } from '@/entities/visit-counsel/model/visit-counsel.types';

/**
 * counsel-start-flow transform1의 출력 모양.
 * FlowDocument의 transform1.outputKey = "departure"로 저장된다.
 *
 * SuffixInput (SuffixValue = string | null) 이 값을 plain string으로 저장하므로
 * dong / floorOrUnit.value 는 runtime에서 string일 수 있다.
 * mapCounselStartDeparture 는 양쪽 형태를 모두 지원한다.
 */
export interface CounselStartDeparture {
  roadAddress: string;
  buildingName?: string | null;
  /** runtime: string | null  /  legacy: { value: string; suffix: '동' } | null */
  dong?: { value: string; suffix: '동' } | string | null;
  floorOrUnit?:
    | { type: 'Unit'; value: { value: string; suffix: '호' } | string }
    | { type: 'Floor'; value: { value: string; suffix: '층' } | string }
    | null;
}

/**
 * value 필드를 plain string 또는 { value: string } 객체에서 안전하게 추출.
 * SuffixInput이 string을 반환하므로 두 형태를 모두 처리한다.
 */
function extractStringValue(v: unknown): string | null {
  if (v == null) return null;
  if (typeof v === 'string') return v || null;
  if (typeof v === 'object' && 'value' in (v as object)) {
    const inner = (v as { value: unknown }).value;
    if (typeof inner === 'string') return inner || null;
    if (typeof inner === 'number') return String(inner) || null;
  }
  return null;
}

/**
 * counsel-start-flow transform1 출력(departure) →
 * POST /api/v1/visit-counsels/{id}/start 요청 페이로드로 변환.
 */
export function mapCounselStartDeparture(departure: unknown): StartVisitCounselDepartureAddress {
  const d = departure as CounselStartDeparture;

  const unit = d.floorOrUnit?.type === 'Unit' ? extractStringValue(d.floorOrUnit.value) : null;
  const floor = d.floorOrUnit?.type === 'Floor' ? extractStringValue(d.floorOrUnit.value) : null;

  return {
    roadAddress: d.roadAddress,
    buildingName: d.buildingName ?? null,
    dong: extractStringValue(d.dong),
    unit,
    floor,
  };
}
